<template>
  <div class="animated fadeIn">
    <div class="kt-portlet kt-portlet--mobile" style="position:relative">
      <div class="kt-portlet__body kt-portlet__body--fit" ref="cnt">
        <div class="m-4">
          <div class="filters-container">
            <TableFilters v-model="filterOptions" style="margin-right: 45px" @click="refresh" />
            <DropdownColumns v-model="fields" class="customize" />
          </div>
          <b-table
            id="audits-table"
            :busy.sync="isBusy"
            show-empty
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :hover="true"
            :striped="true"
            stacked="md"
            v-model="copy"
            :items="provider"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
            @row-clicked="rowClicked"
            tbody-tr-class="tr-pointer"
            v-columns-resizable="{afterResize: columnsWidthChanged}"
          >
            <template v-slot:empty="scope">
              <div class="text-center">{{ $t(scope.emptyText) }}</div>
            </template>

            <template v-slot:head(action)="data">{{ $t(data.label) }}</template>
            <template v-slot:head(action_type)="data">{{ $t(data.label) }}</template>
            <template v-slot:head(timestamp)="data">{{ $t(data.label) }}</template>
            <template v-slot:head(ip)="data">{{ $t(data.label) }}</template>
            <template v-slot:head(affected_data)="data">{{ $t(data.label) }}</template>

            <template v-slot:cell(euser_id)="data">
              <router-link :to="`/users/${data.value}`" v-if="data.value">{{data.value}}</router-link>
            </template>
            <template v-slot:cell(username)="data">
              <router-link
                :to="`/users/${data.item.euser_id}`"
                v-if="data.item.euser_id"
              >{{data.item.username ? data.item.username : data.item.euser_id}}</router-link>
            </template>

            <template v-slot:cell(affected_data)="data">
              <div v-if="data.item.action == 'LotoEventClearResult'">
                <LotoEventClearResult :json="data.value" />
              </div>
              <div v-else>{{ data.value }}</div>
            </template>
            <template v-slot:cell(timestamp)="data">
              <span>{{ mom_unix(data.value).format(tableDateLong) }}</span>
            </template>
          </b-table>
          <div class="container-fluid">
            <div class="row">
              <div style="white-space:nowrap;" class="my-2">
                <PaginationSelect
                  v-model="perPage"
                  :options="paginationOpts"
                  variant="light"
                  size="sm"
                />
                <span>
                  &nbsp;
                  <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                    <span slot="start">{{(currentPage-1) * perPage + 1}}</span>
                    <span slot="end">{{currentPage * perPage}}</span>
                    <span slot="total">{{totalRows}}</span>
                  </i18n>
                </span>
                <b-button
                  size="sm"
                  @click.stop="$root.$emit('bv::refresh::table', 'audits-table')"
                  class="ml-2 refresh"
                  variant="light"
                  :title="$t('Refresh')"
                >
                  <i class="icon-refresh"></i>
                </b-button>
              </div>
              <nav class="ml-auto mt-2">
                <b-pagination
                  size="md"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  v-model="currentPage"
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal id="modal-edit" size="lg" scrollable>
      <template slot="modal-title">{{$t('Audit')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <Audit :selectedeid="selectedeid" />
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button variant="secondary" @click="cancel()">{{$t('Cancel')}}</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import startCase from "@/shared/strings";
import { errorConf } from "@/shared/notifications";
import tableMixin from "@/shared/mixins/table.mixin";
import PaginationSelect from "@/components/PaginationSelect";
import DropdownColumns from "@/components/DropdownColumns";
import TableFilters from "@/components/TableFilters";
import Audit from "./Audit";
import LotoEventClearResult from "./formats/LotoEventClearResult";

export default {
  name: "audits",
  mixins: [tableMixin],
  components: {
    PaginationSelect,
    DropdownColumns,
    TableFilters,
    Audit,
    LotoEventClearResult
  },
  data: function() {
    return {
      tableName: "audits",
      filterOptions: [
        {
          field: "timestamp",
          type: "date",
          opens: "right",
          selected: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          default: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          ranges: {
            "Last hour": [moment().subtract(1, "hour"), moment()],
            "Last 6 hours": [moment().subtract(6, "hours"), moment()],
            "Last 24 hours": [moment().subtract(24, "hours"), moment()],
            Today: [moment().startOf("day"), moment().endOf("day")],
            Yesterday: [
              moment()
                .subtract(1, "days")
                .startOf("day"),
              moment()
                .subtract(1, "days")
                .endOf("day")
            ],
            "This week": [
              moment().startOf("isoWeek"),
              moment().endOf("isoWeek")
            ],
            "Last week": [
              moment()
                .subtract(1, "week")
                .startOf("isoWeek"),
              moment()
                .subtract(1, "week")
                .endOf("isoWeek")
            ],
            "This month": [moment().startOf("month"), moment().endOf("month")],
            "Last month": [
              moment()
                .subtract(1, "month")
                .startOf("month"),
              moment()
                .subtract(1, "month")
                .endOf("month")
            ],
            "This year": [moment().startOf("year"), moment().endOf("year")],
            "Last year": [moment().subtract(1, "year"), moment()]
          }
        }
      ],
      isBusy: false,
      sortBy: "timestamp",
      sortDesc: true,
      fields: [
        {
          key: "eid",
          label: "Id",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        {
          key: "euser_id",
          label: "User Id",
          sortable: true,
          class: "d-none",
          ignore: true
        },
        { key: "username", sortable: true, class: "" },
        { key: "action", sortable: true, class: "" },
        { key: "action_type", sortable: true, class: "" },
        { key: "app_id", sortable: true, class: "d-none", ignore: true },
        { key: "timestamp", sortable: true, ignore: true, class: "" },
        { key: "ip", label: "IP", sortable: true, class: "" },
        { key: "result", sortable: false, class: "d-none" },
        { key: "affected_data", sortable: false, class: "d-none" }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      filterField: null,
      checkAll: false,
      copy: null,
      addLabel: "",
      selectedeid: ""
    };
  },
  methods: {
    refresh() {
      this.$root.$emit("bv::refresh::table", "audits-table");
    },
    provider(ctx, callback) {
      const loading = this.$loading.open({
        container: this.$refs.cnt
      });

      const paginationData = {
        rowsPerPage: this.perPage,
        page: this.currentPage
      };

      if (this.filter && this.filterField) {
        paginationData.likeValues = [this.filter];
        paginationData.likeFields = [this.filterField];
      } else {
        paginationData.likeValues = [];
        paginationData.likeFields = [];
      }

      if (this.sortBy) {
        let sortBy = this.sortBy;
        if (sortBy == "euser_id") sortBy = "user_id";
        paginationData.sortBy = [sortBy];
        paginationData.sortDirection = [this.sortDesc ? "DESC" : "ASC"];
      }

      const exactFields = [];
      const exactValues = [];

      this.filterOptions.forEach(item => {
        if (item.type != "date" && item.selected.length) {
          exactFields.push(item.field);
          exactValues.push(item.selected);
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            paginationData.dateField = item.field;
            paginationData.dateStart = item.selected.startDate;
            paginationData.dateEnd = item.selected.endDate;
          }
        }
      });

      if (exactFields.length) {
        paginationData.exactFields = exactFields;
        paginationData.exactValues = exactValues;
      }

      this.$store
        .dispatch("audit/fetch", paginationData)
        .then(resp => {
          this.totalRows = resp.data.total;
          resp.data.rows.forEach((item, index) => {
            resp.data.rows[index]["_cellVariants"] = {};
            if (item.result == "success") {
              resp.data.rows[index]["_cellVariants"]["result"] = "success";
            } else if (item.result == "error") {
              resp.data.rows[index]["_cellVariants"]["result"] = "danger";
            }
          });
          callback(resp.data.rows);
        })
        .catch(err => {
          this.$bvToast.toast(
            this.$t(err.message),
            errorConf(this.$t("Error"))
          );
          this.totalRows = 0;
          callback([]);
        })
        .finally(() => {
          loading.close();
        });

      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    rowClicked(item, index, event) {
      this.selectedeid = item.eid;
      this.$bvModal.show("modal-edit");
      return;
      //this.$router.push("/audits/" + item.eid);
    },
    mom_unix(val) {
      return moment.unix(val / 1e9);
    },
    redirect() {}
  }
};
</script>

<style lang="scss" scoped>
</style>


